import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Store, Loader2, PackageOpen, Sparkles, GraduationCap } from "lucide-react";
import { MKT_GOLD, MKT_TINT } from "../../lib/marketplace";
import { apiPublicHome, apiPublicItems } from "../../lib/marketplaceApi";
import { CatalogCard, TiendaCard, CATALOG_CATS } from "./publicUi";
import Seo from "../../components/Seo";

export default function MarketplacePublic() {
  const [home, setHome] = useState(null);
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState("");
  const [tipo, setTipo] = useState("all");
  const [sort, setSort] = useState("recientes");
  const [resultados, setResultados] = useState(null);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => { apiPublicHome().then(setHome).catch(() => setHome({})); }, []);

  const filtrando = q.trim() !== "" || categoria !== "" || tipo !== "all" || sort !== "recientes";

  const buscar = useCallback(async () => {
    setBuscando(true);
    try {
      const data = await apiPublicItems({ q, categoria, tipo, sort, page: 1, limit: 24 });
      setResultados(data.items);
    } catch { setResultados([]); }
    finally { setBuscando(false); }
  }, [q, categoria, tipo, sort]);

  useEffect(() => {
    if (!filtrando) { setResultados(null); return; }
    const t = setTimeout(buscar, 350);
    return () => clearTimeout(t);
  }, [buscar, filtrando]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketplace-public">
      <Seo title="Marketplace de recursos digitales para la construcción" description="Compra y vende plantillas, software, recursos BIM/CAD y cursos independientes para la industria de la construcción en ConstruLink Marketplace." />
      {/* Hero */}
      <header className="text-white" style={{ background: `linear-gradient(135deg, #1E293B, #0F172A)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: MKT_GOLD }}>Pilar 04</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">ConstruLink Marketplace</h1>
          <p className="mt-3 text-slate-300 text-lg max-w-2xl">Recursos digitales creados por profesionales para la construcción.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Busca por título, descripción, etiquetas o vendedor…"
                data-testid="mkt-buscador"
                className="w-full rounded-xl bg-white/95 pl-11 pr-4 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <Link to="/marketplace" data-testid="ir-centro-vendedor" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-slate-900 bg-white/90 hover:bg-white transition-colors">
              <Store size={18} /> Vender aquí
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="mkt-filtros">
          <button onClick={() => setCategoria("")} data-testid="filtro-cat-todas" className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors" style={categoria === "" ? { backgroundColor: MKT_GOLD, borderColor: MKT_GOLD, color: "#fff" } : { borderColor: "#E2E8F0", color: "#475569" }}>Todas</button>
          {CATALOG_CATS.map((c) => (
            <button key={c.id} onClick={() => setCategoria(c.id)} data-testid={`filtro-cat-${c.id}`} className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors" style={categoria === c.id ? { backgroundColor: MKT_GOLD, borderColor: MKT_GOLD, color: "#fff" } : { borderColor: "#E2E8F0", color: "#475569" }}>{c.label}</button>
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" />
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="filtro-tipo" className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 outline-none">
            <option value="all">Todo</option>
            <option value="producto">Productos</option>
            <option value="curso">Cursos</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filtro-orden" className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 outline-none">
            <option value="recientes">Más recientes</option>
            <option value="precio_asc">Precio: menor a mayor</option>
            <option value="precio_desc">Precio: mayor a menor</option>
          </select>
        </div>

        {/* Resultados de búsqueda/filtro */}
        {filtrando ? (
          <section data-testid="mkt-resultados">
            <h2 className="text-lg font-extrabold text-slate-900 mb-3">Resultados</h2>
            {buscando ? (
              <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="animate-spin mr-2" /> Buscando…</div>
            ) : resultados && resultados.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {resultados.map((i) => <CatalogCard key={`${i.tipo}-${i.id}`} item={i} />)}
              </div>
            ) : (
              <div className="text-center py-16 text-slate-400"><PackageOpen size={40} className="mx-auto" /><p className="mt-2">No se encontraron resultados.</p></div>
            )}
          </section>
        ) : home === null ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando catálogo…</div>
        ) : (
          <div className="space-y-10">
            <Seccion titulo="Productos destacados" Icon={Sparkles} items={home.destacados_productos} vacio="Aún no hay productos publicados." />
            <Seccion titulo="Cursos independientes destacados" Icon={GraduationCap} items={home.destacados_cursos} vacio="Aún no hay cursos publicados." />

            {home.tiendas_verificadas?.length > 0 && (
              <section data-testid="mkt-tiendas-verificadas">
                <h2 className="text-lg font-extrabold text-slate-900 mb-3 inline-flex items-center gap-2"><Store size={18} style={{ color: MKT_GOLD }} /> Tiendas verificadas</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {home.tiendas_verificadas.map((t) => <TiendaCard key={t.id} tienda={t} />)}
                </div>
              </section>
            )}

            <Seccion titulo="Más recientes" Icon={Sparkles} items={home.recientes} vacio="Aún no hay contenido publicado en el Marketplace." testid="mkt-recientes" />
          </div>
        )}
      </div>
    </div>
  );
}

function Seccion({ titulo, Icon, items, vacio, testid }) {
  return (
    <section data-testid={testid || `sec-${titulo}`}>
      <h2 className="text-lg font-extrabold text-slate-900 mb-3 inline-flex items-center gap-2"><Icon size={18} style={{ color: MKT_GOLD }} /> {titulo}</h2>
      {items && items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((i) => <CatalogCard key={`${i.tipo}-${i.id}`} item={i} />)}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-400 text-sm">{vacio}</div>
      )}
    </section>
  );
}
